/**
 * Platform seams of the online client (tests inject fakes): WebSocket, RTCPeerConnection, clock, timers, random.
 * The browser implementation is apps/client `src/net/browser-env.ts` (`browserNetEnv`); a native shell provides
 * its own (react-native-webrtc, RN's WebSocket).
 */
import type { ClientPlatform } from '@hf/shared';
import type { PlatformConfig } from '../platform';

/** The subset of the browser WebSocket the client uses. */
export interface WebSocketLike {
  readonly readyState: number;
  binaryType: string;
  readonly bufferedAmount: number;
  send(data: string | ArrayBufferLike | ArrayBufferView): void;
  close(code?: number, reason?: string): void;
  onopen: ((ev: unknown) => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
  onclose: ((ev: { code: number; reason: string }) => void) | null;
  onerror: ((ev: unknown) => void) | null;
}

export const WS_OPEN = 1;

export type TimerId = ReturnType<typeof setTimeout>;

export interface NetEnv {
  createSocket(url: string): WebSocketLike;
  /** RTCPeerConnection constructor, or null when WebRTC is unavailable (→ relay only). */
  RTCPeerConnection: (new (config: RTCConfiguration) => RTCPeerConnection) | null;
  /** Monotonic ms (performance.now()). */
  now(): number;
  setTimeout(fn: () => void, ms: number): TimerId;
  clearTimeout(id: TimerId): void;
  random(): number;
  /** Diagnostics (default console). */
  log: { info(...a: unknown[]): void; warn(...a: unknown[]): void };
  /** `hello.platform` (default 'web'): the server logs it and records it on matches (cross-play analysis). */
  platform?: ClientPlatform;
  /** `hello.appVersion`: the native shell's store version (absent on web). */
  appVersion?: string;
  /** `hello.clientVersion` override (default: `clientVersionFor(getPlatform().config)`, e.g. the Vite build id). */
  clientVersion?: string;
}

/** Client build id for `hello.clientVersion`: the CI build id, else "0.1.0-web" (production) / "0.1.0-dev". */
export const clientVersionFor = (config: Pick<PlatformConfig, 'buildId' | 'mode'>): string => {
  const id = config.buildId;
  const v =
    typeof id === 'string' && id.length > 0 ? id : `0.1.0-${config.mode === 'production' ? 'web' : 'dev'}`;
  return v.slice(0, 64);
};
